import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { ApiResponse } from "../types/api";
import { notify } from "../context/FeedbackContext";

const DEV_FALLBACK_BACKEND_URL = "http://localhost:8000/api/v1";
const PROD_DEFAULT_BACKEND_URL = "https://cms.prepstripe.com/api/v1";

const normalizeBackendUrl = (url: string): string => {
  const trimmedUrl = url.replace(/\/+$/, "");

  if (trimmedUrl.endsWith("/api/v1")) {
    return trimmedUrl;
  }

  if (trimmedUrl.endsWith("/api")) {
    return `${trimmedUrl}/v1`;
  }

  return `${trimmedUrl}/api/v1`;
};

/**
 * Resolve the backend base URL.
 * - Prioritizes VITE_API_URL if set.
 * - In development, falls back to http://localhost:8000/api/v1.
 * - In production, falls back to https://cms.prepstripe.com/api/v1.
 */
const resolveBackendUrl = (): string => {
  const rawEnvUrl: unknown = import.meta.env.VITE_API_URL;
  const envUrl: string | undefined =
    typeof rawEnvUrl === "string" ? rawEnvUrl : undefined;

  if (envUrl !== undefined && envUrl.trim().length > 0) {
    return normalizeBackendUrl(envUrl.trim());
  }

  if (import.meta.env.DEV) {
    return DEV_FALLBACK_BACKEND_URL;
  }

  return PROD_DEFAULT_BACKEND_URL;
};

// Create an Axios instance with a base URL for v1 API routes
const apiBaseUrl = resolveBackendUrl();
const authBaseUrl = apiBaseUrl.replace(/\/v1$/, "");

const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create a separate Axios instance for auth endpoints (at /api level, not /api/v1)
export const authApi: AxiosInstance = axios.create({
  baseURL: authBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token if available
const addAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = localStorage.getItem("token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken, (error: unknown) => {
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});

// Add auth token to authApi requests as well
authApi.interceptors.request.use(addAuthToken, (error: unknown) => {
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
});

const isAxiosErrorWithStatus = (error: unknown): error is AxiosError<unknown, unknown> =>
  axios.isAxiosError(error);

interface SessionExpiredContext {
  status: number;
  config: { url?: string };
}

const extractSessionExpiredContext = (
  error: unknown
): SessionExpiredContext | null => {
  if (!isAxiosErrorWithStatus(error)) {
    return null;
  }
  const status = error.response?.status ?? 0;
  if (status !== 401) {
    return null;
  }
  const configUrl = error.config?.url;
  return { status, config: { url: configUrl } };
};

// Response interceptor - Handle common errors (401, 403, 429, etc.)
const handleResponseError = (error: unknown): Promise<unknown> => {
  const ctx = extractSessionExpiredContext(error);
  const requestUrl = String(ctx?.config?.url ?? "");

  if (ctx !== null) {
    const isLoginRequest = requestUrl.endsWith("/login");

    if (isLoginRequest) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    localStorage.removeItem("token");

    if (window.location.pathname !== "/login") {
      sessionStorage.setItem("session_expired", "1");
      sessionStorage.setItem(
        "redirect_after_login",
        `${window.location.pathname}${window.location.search}`
      );
      notify.error("Your session expired. Please sign in again.", "Session expired");
      window.location.assign("/login");
    }

    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }

  // Handle 402 Subscription/Trial expired: backend gates every tenant.active
  // endpoint behind this. Send the user to the billing screen instead of
  // letting each request fail silently.
  if (isAxiosErrorWithStatus(error) && error.response?.status === 402) {
    const payload = error.response.data as { message?: string } | undefined;
    const onSubscriptionPage =
      window.location.pathname.startsWith("/admin/subscription");
    if (!onSubscriptionPage && !requestUrl.endsWith("/login")) {
      sessionStorage.setItem("subscription_expired", "1");
      notify.error(
        payload?.message ||
          "Your organization's subscription or free trial has expired. Please upgrade to continue.",
        "Subscription expired"
      );
      window.location.assign("/admin/subscription");
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }

  // Handle 429 Too Many Requests
  if (isAxiosErrorWithStatus(error) && error.response?.status === 429) {
    const retryAfter = error.response.headers?.["retry-after"];
    const message = retryAfter
      ? `Too many requests. Please wait ${retryAfter} seconds before trying again.`
      : "Too many requests. Please slow down and try again shortly.";
    notify.error(message, "Rate limit exceeded");
  }

  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
};

/**
 * Back-compat pagination bridge.
 *
 * The backend returns list pagination under `meta.pagination` (see the
 * `HandlesApiResponses` trait). Newer code reads that directly, but the legacy
 * `context/Admin/*` providers read a top-level `response.data.pagination`.
 * To keep both working during the react-query migration, mirror
 * `meta.pagination` onto a top-level `pagination` field when it is absent.
 * This never overwrites an existing value and leaves `data` untouched.
 */
const bridgePagination = (
  response: AxiosResponse<ApiResponse<unknown>>
): AxiosResponse<ApiResponse<unknown>> => {
  const body = response.data as
    | (ApiResponse<unknown> & { pagination?: unknown })
    | undefined;
  const metaPagination = body?.meta?.pagination;
  if (body && metaPagination !== undefined && body.pagination === undefined) {
    body.pagination = metaPagination;
  }
  return response;
};

api.interceptors.response.use(bridgePagination, handleResponseError);

// Add response interceptor to authApi as well for consistency
authApi.interceptors.response.use(bridgePagination, handleResponseError);

export default api;

